/**
 * Central LOC Registry (Location of Coverage Map)
 *
 * This file provides a centralized map of all LOC-specific modules and configurations.
 * Currently Cancer and Hospital Indemnity use this new pattern. Other LOCs remain in the old pattern
 * (hardcoded in client.ts) until they are migrated.
 *
 * Each LOC entry provides:
 * - definitions: Benefit definitions for the LOC
 * - skeleton: Function to get skeleton data
 * - transformToApi: Transform function from grid format to API format
 * - transformFromApi: Transform function from API format to grid format
 * - validFields: Set of valid field names for API filtering
 * - objectType: The objectType discriminator value for this LOC
 * - coverageTypes: Array of coverage type strings that map to this LOC
 */

import { type BenefitTransformToApiOptions } from "@platform/btx-benefit-transforms";
import { CoverageTypeEnum } from "@platform/types";

import {
  benefits as cancerBenefits,
  getCancerSkeleton,
  transformCancerBenefitsToApi,
  transformCancerBenefitsFromApi,
  CANCER_VALID_FIELDS,
} from "./cancer";
import {
  benefits as hospitalIndemnityBenefits,
  getHospitalIndemnitySkeleton,
  transformHospitalIndemnityBenefitsToApi,
  transformHospitalIndemnityBenefitsFromApi,
  HOSPITAL_INDEMNITY_VALID_FIELDS,
} from "./hospital-indemnity";
import {
  benefits as visionBenefits,
  getVisionSkeleton,
  transformVisionBenefitsToApi,
  transformVisionBenefitsFromApi,
  VISION_VALID_FIELDS,
} from "./vision";
import {
  benefits as basicLifeBenefits,
  getBasicLifeSkeleton,
  transformBasicLifeBenefitsToApi,
  transformBasicLifeBenefitsFromApi,
  BASIC_LIFE_VALID_FIELDS,
} from "./basic-life";
import {
  benefits as voluntaryLifeBenefits,
  getVoluntaryLifeSkeleton,
  transformVoluntaryLifeBenefitsToApi,
  transformVoluntaryLifeBenefitsFromApi,
  VOLUNTARY_LIFE_VALID_FIELDS,
} from "./voluntary-life";
import {
  stdBenefitDefinitions as stdBenefits,
  getStdSkeleton,
  transformStdBenefitsToApi,
  transformStdBenefitsFromApi,
  STD_VALID_FIELDS,
} from "./std";
import {
  ltdBenefitDefinitions as ltdBenefits,
  getLtdSkeleton,
  transformLtdBenefitsToApi,
  transformLtdBenefitsFromApi,
  LTD_VALID_FIELDS,
} from "./ltd";
import {
  benefits as criticalIllnessBenefits,
  getCriticalIllnessSkeleton,
  transformCriticalIllnessBenefitsToApi,
  transformCriticalIllnessBenefitsFromApi,
  CRITICAL_ILLNESS_VALID_FIELDS,
} from "./critical-illness";
import {
  dentalHmoBenefitDefinitions as dentalHmoBenefits,
  getDentalHmoSkeleton,
  transformDentalHmoBenefitsToApi,
  transformDentalHmoBenefitsFromApi,
  DENTAL_HMO_VALID_FIELDS,
} from "./dental-hmo";
import {
  dentalPpoBenefitDefinitions as dentalPpoBenefits,
  getDentalPpoSkeleton,
  transformDentalPpoBenefitsToApi,
  transformDentalPpoBenefitsFromApi,
  DENTAL_PPO_VALID_FIELDS,
} from "./dental-ppo";
import {
  benefits as accidentBenefits,
  getAccidentSkeleton,
  transformAccidentBenefitsToApi,
  transformAccidentBenefitsFromApi,
  ACCIDENT_VALID_FIELDS,
} from "./accident";
import {
  benefits as eapBenefits,
  getEapSkeleton,
  transformEapBenefitsToApi,
  transformEapBenefitsFromApi,
  EAP_VALID_FIELDS,
} from "./eap";
import {
  benefits as wholeLifeBenefits,
  getWholeLifeSkeleton,
  transformWholeLifeBenefitsToApi,
  transformWholeLifeBenefitsFromApi,
  WHOLE_LIFE_VALID_FIELDS,
} from "./whole-life";
import {
  benefits as individualDisabilityBenefits,
  getIndividualDisabilitySkeleton,
  transformIndividualDisabilityBenefitsToApi,
  transformIndividualDisabilityBenefitsFromApi,
  INDIVIDUAL_DISABILITY_VALID_FIELDS,
} from "./individual-disability";
import {
  benefits as fmlBenefits,
  getFmlSkeleton,
  transformFmlBenefitsToApi,
  transformFmlBenefitsFromApi,
  FML_VALID_FIELDS,
} from "./fml";

import type { BenefitDefinitions } from "../benefits/benefit.types";
import type { BenefitsApi, BenefitsGrid } from "./types";
import type { RfpCoverage, RfpDentalBenefit } from "../types/rfp.api.types";
import type { CancerBenefitsApi, CancerBenefitsGrid } from "./cancer/types";
import type {
  HospitalIndemnityBenefitsApi,
  HospitalIndemnityBenefitsGrid,
} from "./hospital-indemnity/types";
import type { VisionBenefitsApi, VisionBenefitsGrid } from "./vision/types";
import type {
  BasicLifeAdndBenefitsApi,
  BasicLifeAdndBenefitsGrid,
} from "./basic-life/types";
import type {
  VoluntaryLifeBenefitsApi,
  VoluntaryLifeBenefitsGrid,
} from "./voluntary-life/types";
import type { StdBenefitApi, StdBenefitGrid } from "./std/types";
import type { LtdBenefitApi, LtdBenefitGrid } from "./ltd/types";
import type {
  CriticalIllnessBenefitsApi,
  CriticalIllnessBenefitsGrid,
} from "./critical-illness/types";
import type {
  DentalHmoBenefitApi,
  DentalHmoBenefitGrid,
} from "./dental-hmo/types";
import type { DentalPpoBenefitGrid } from "./dental-ppo/types";
import type {
  AccidentBenefitsApi,
  AccidentBenefitsGrid,
} from "./accident/types";
import type { EapBenefitsApi, EapBenefitsGrid } from "./eap/types";
import type {
  WholeLifeBenefitsApi,
  WholeLifeBenefitsGrid,
} from "./whole-life/types";
import type {
  IndividualDisabilityBenefitsApi,
  IndividualDisabilityBenefitsGrid,
} from "./individual-disability/types";
import type { FmlBenefitsApi, FmlBenefitsGrid } from "./fml/types";
/**
 * LOC configuration interface
 *
 * Generic interface that accepts specific benefit types for each LOC.
 * Each LOC provides its own specific Grid and Api types that are part of the discriminated unions.
 *
 * @template TGrid - The grid format type for this LOC (e.g., CancerBenefitsGrid)
 * @template TApi - The API format type for this LOC (e.g., CancerBenefitsApi)
 */
export interface LocConfig<
  TGrid extends BenefitsGrid = BenefitsGrid,
  TApi extends BenefitsApi = BenefitsApi,
> {
  definitions: BenefitDefinitions;
  skeleton: () => Partial<RfpCoverage>;
  /**
   * `options` carries the saving surface's intent for LOCs that can't infer it
   * from the data alone (see BenefitTransformToApiOptions). Every LOC whose
   * transform has no such ambiguity simply declares one parameter and ignores
   * it — a narrower function still satisfies this type.
   */
  transformToApi: (
    benefits: TGrid,
    options?: BenefitTransformToApiOptions,
  ) => TApi;
  transformFromApi: (benefits: TApi) => TGrid;
  validFields: Set<string>;
  objectType: string;
}

/**
 * Coverage types that have LOC configurations
 * DENTAL is excluded as it's handled separately
 */
export type LocCoverageType = Exclude<
  CoverageTypeEnum,
  CoverageTypeEnum.DENTAL
>;

/**
 * Central LOC registry
 *
 * Cancer, Hospital Indemnity, Vision, Basic Life, Voluntary Life, STD, LTD, Critical Illness, Dental HMO, Accident, EAP, Whole Life, Individual Disability, and FML use this new pattern. Other LOCs remain in old pattern.
 *
 * Usage:
 * ```typescript
 * const config = LOC_MAP.cancer;
 * const transformed = config.transformToApi(benefits);
 * ```
 */
export const LOC_MAP = {
  [CoverageTypeEnum.CANCER]: {
    definitions: cancerBenefits,
    skeleton: getCancerSkeleton,
    transformToApi: transformCancerBenefitsToApi,
    transformFromApi: transformCancerBenefitsFromApi,
    validFields: CANCER_VALID_FIELDS,
    objectType: "cancer",
  } as LocConfig<CancerBenefitsGrid, CancerBenefitsApi>,
  [CoverageTypeEnum.HOSPITAL_INDEMNITY]: {
    definitions: hospitalIndemnityBenefits,
    skeleton: getHospitalIndemnitySkeleton,
    transformToApi: transformHospitalIndemnityBenefitsToApi,
    transformFromApi: transformHospitalIndemnityBenefitsFromApi,
    validFields: HOSPITAL_INDEMNITY_VALID_FIELDS,
    objectType: "hospitalIndemnity",
  } as LocConfig<HospitalIndemnityBenefitsGrid, HospitalIndemnityBenefitsApi>,
  [CoverageTypeEnum.VISION]: {
    definitions: visionBenefits,
    skeleton: getVisionSkeleton,
    transformToApi: transformVisionBenefitsToApi,
    transformFromApi: transformVisionBenefitsFromApi,
    validFields: VISION_VALID_FIELDS,
    objectType: "vision",
  } as LocConfig<VisionBenefitsGrid, VisionBenefitsApi>,
  [CoverageTypeEnum.BASIC_LIFE_ADND]: {
    definitions: basicLifeBenefits,
    skeleton: getBasicLifeSkeleton,
    transformToApi: transformBasicLifeBenefitsToApi,
    transformFromApi: transformBasicLifeBenefitsFromApi,
    validFields: BASIC_LIFE_VALID_FIELDS,
    objectType: "basicLifeAdnd",
  } as LocConfig<BasicLifeAdndBenefitsGrid, BasicLifeAdndBenefitsApi>,
  [CoverageTypeEnum.VOLUNTARY_LIFE_ADND]: {
    definitions: voluntaryLifeBenefits,
    skeleton: getVoluntaryLifeSkeleton,
    transformToApi: transformVoluntaryLifeBenefitsToApi,
    transformFromApi: transformVoluntaryLifeBenefitsFromApi,
    validFields: VOLUNTARY_LIFE_VALID_FIELDS,
    objectType: "voluntaryLifeAdnd",
  } as LocConfig<VoluntaryLifeBenefitsGrid, VoluntaryLifeBenefitsApi>,
  [CoverageTypeEnum.STD]: {
    definitions: stdBenefits,
    skeleton: getStdSkeleton,
    transformToApi: transformStdBenefitsToApi,
    transformFromApi: transformStdBenefitsFromApi,
    validFields: STD_VALID_FIELDS,
    objectType: "std",
  } as LocConfig<StdBenefitGrid, StdBenefitApi>,
  [CoverageTypeEnum.LTD]: {
    definitions: ltdBenefits,
    skeleton: getLtdSkeleton,
    transformToApi: transformLtdBenefitsToApi,
    transformFromApi: transformLtdBenefitsFromApi,
    validFields: LTD_VALID_FIELDS,
    objectType: "ltd",
  } as LocConfig<LtdBenefitGrid, LtdBenefitApi>,
  [CoverageTypeEnum.CRITICAL_ILLNESS]: {
    definitions: criticalIllnessBenefits,
    skeleton: getCriticalIllnessSkeleton,
    transformToApi: transformCriticalIllnessBenefitsToApi,
    transformFromApi: transformCriticalIllnessBenefitsFromApi,
    validFields: CRITICAL_ILLNESS_VALID_FIELDS,
    objectType: "criticalIllness",
  } as LocConfig<CriticalIllnessBenefitsGrid, CriticalIllnessBenefitsApi>,
  [CoverageTypeEnum.DENTAL_HMO]: {
    definitions: dentalHmoBenefits,
    skeleton: getDentalHmoSkeleton,
    transformToApi: transformDentalHmoBenefitsToApi,
    transformFromApi: transformDentalHmoBenefitsFromApi,
    validFields: DENTAL_HMO_VALID_FIELDS,
    objectType: "dental",
  } as LocConfig<DentalHmoBenefitGrid, DentalHmoBenefitApi>,
  [CoverageTypeEnum.DENTAL_PPO]: {
    definitions: dentalPpoBenefits,
    skeleton: getDentalPpoSkeleton,
    transformToApi: transformDentalPpoBenefitsToApi,
    transformFromApi: transformDentalPpoBenefitsFromApi,
    validFields: DENTAL_PPO_VALID_FIELDS,
    objectType: "dental",
    coverageTypes: [CoverageTypeEnum.DENTAL_PPO, "dental-ppo", "dentalPpo"],
  } as LocConfig<DentalPpoBenefitGrid, RfpDentalBenefit>,
  [CoverageTypeEnum.ACCIDENT]: {
    definitions: accidentBenefits,
    skeleton: getAccidentSkeleton,
    transformToApi: transformAccidentBenefitsToApi,
    transformFromApi: transformAccidentBenefitsFromApi,
    validFields: ACCIDENT_VALID_FIELDS,
    objectType: "accident",
  } as LocConfig<AccidentBenefitsGrid, AccidentBenefitsApi>,
  [CoverageTypeEnum.EAP]: {
    definitions: eapBenefits,
    skeleton: getEapSkeleton,
    transformToApi: transformEapBenefitsToApi,
    transformFromApi: transformEapBenefitsFromApi,
    validFields: EAP_VALID_FIELDS,
    objectType: "eap",
  } as LocConfig<EapBenefitsGrid, EapBenefitsApi>,
  [CoverageTypeEnum.WHOLE_LIFE]: {
    definitions: wholeLifeBenefits,
    skeleton: getWholeLifeSkeleton,
    transformToApi: transformWholeLifeBenefitsToApi,
    transformFromApi: transformWholeLifeBenefitsFromApi,
    validFields: WHOLE_LIFE_VALID_FIELDS,
    objectType: "wholeLife",
  } as LocConfig<WholeLifeBenefitsGrid, WholeLifeBenefitsApi>,
  [CoverageTypeEnum.INDIVIDUAL_DISABILITY_INSURANCE]: {
    definitions: individualDisabilityBenefits,
    skeleton: getIndividualDisabilitySkeleton,
    transformToApi: transformIndividualDisabilityBenefitsToApi,
    transformFromApi: transformIndividualDisabilityBenefitsFromApi,
    validFields: INDIVIDUAL_DISABILITY_VALID_FIELDS,
    objectType: "individualDisability",
  } as LocConfig<
    IndividualDisabilityBenefitsGrid,
    IndividualDisabilityBenefitsApi
  >,
  [CoverageTypeEnum.FMLA]: {
    definitions: fmlBenefits,
    skeleton: getFmlSkeleton,
    transformToApi: transformFmlBenefitsToApi,
    transformFromApi: transformFmlBenefitsFromApi,
    validFields: FML_VALID_FIELDS,
    objectType: "fml",
  } as LocConfig<FmlBenefitsGrid, FmlBenefitsApi>,
};
